
import { defineComponent, reactive, ref, computed, onMounted } from 'vue';
import { message, Modal } from 'ant-design-vue';
import moment, { Moment } from 'moment';
import store from '@/store';
import { noEmptyProp, not, dispatchDownload } from '@/utils/illuminate';
import type { TableColumn } from '@/components/Table';
import { request } from '@/utils/request';
import { createApiUrl } from '@/utils/utils';
import { useUtils } from '@/core/utils';
import { exportTaskStatusOptions, exportTaskStatusTagMap } from './dict';

type FilterParamsType = {
  exportType: string;
  createTime: Moment[];
  status: '' | number;
};

type ExportTypeOption = {
  label: string;
  value: string;
};

type ExportTypeRes = {
  type: string;
  desc: string;
};

export default defineComponent({
  name: 'ExportCenter',
  setup() {
    const utils = useUtils();
    const filterParams = reactive<FilterParamsType>({
      exportType: '',
      createTime: [],
      status: '',
    });
    const taskTypeOptions = ref<ExportTypeOption[]>([{ label: '全部', value: '' }]);

    const searchList = computed(() => [
      {
        label: '任务类型',
        name: 'exportType',
        type: 'select',
        placeholder: '全部',
        allowClear: true,
        fieldNames: { label: 'label', value: 'value' },
        sel_data: taskTypeOptions.value,
        width: '220px',
      },
      {
        label: '创建时间',
        name: 'createTime',
        type: 'rangepicker',
        format: 'YYYY-MM-DD',
        placeholder: ['开始日期', '结束日期'],
        width: '260px',
      },
      {
        label: '状态',
        name: 'status',
        type: 'select',
        placeholder: '全部',
        allowClear: true,
        fieldNames: { label: 'label', value: 'value' },
        sel_data: exportTaskStatusOptions,
        width: '160px',
      },
    ]);

    const tableRef = ref<any>(null);
    const tableColumns: TableColumn[] = [
      { title: '任务类型', dataIndex: 'exportTypeName', width: '220px', align: 'center' },
      { title: '任务名称', dataIndex: 'exportName', width: '320px', align: 'center' },
      { title: '创建时间', dataIndex: 'createTime', width: '200px', align: 'center' },
      {
        title: '状态',
        dataIndex: 'status',
        width: '120px',
        align: 'center',
        customRender: utils.createTagSlot(exportTaskStatusTagMap),
      },
      { title: '操作', dataIndex: 'action', width: '180px', slots: { customRender: 'action' }, fixed: 'right', align: 'center' },
    ];

    const loadTaskTypes = async () => {
      try {
        const { data } = await request.get(createApiUrl('/newlinkSass/sass/export/types'));
        taskTypeOptions.value = [{ label: '全部', value: '' }, ...(data || []).map((item: ExportTypeRes) => ({ label: item.desc, value: item.type }))];
      } catch (e: Error & any) {
        message.error(e.message);
      }
    };

    const loadTableData = async (parameter: any) => {
      const params: Record<string, any> = {
        enterpriseId: store.state.User!.enterprise?.enterpriseId,
        ...parameter,
        ...noEmptyProp(not(filterParams, ['createTime'])),
      };
      if (filterParams.createTime.length) {
        params.createTimeStart = moment(filterParams.createTime[0]).format('YYYY-MM-DD 00:00:00');
        params.createTimeEnd = moment(filterParams.createTime[1]).format('YYYY-MM-DD 23:59:59');
      }
      const { data } = await request.post(createApiUrl('/newlinkSass/sass/export/task/list'), params);
      return data;
    };

    const onRefreshTable = () => {
      tableRef.value.refresh(true);
    };

    const handleDownload = (record: Record<string, any>) => {
      if (!record.downloadUrl) return;
      dispatchDownload(record.exportName || '导出文件', record.downloadUrl);
    };

    const handleRemove = (record: Record<string, any>) => {
      Modal.confirm({
        title: '删除导出任务',
        content: '删除后不可恢复，确定删除吗？',
        okText: '确定',
        cancelText: '取消',
        async onOk() {
          try {
            await request.post(createApiUrl('/newlinkSass/sass/export/task/remove'), {}, { params: { taskId: record.taskId } });
            message.success('删除成功');
            onRefreshTable();
          } catch (e: Error & any) {
            message.error(e.message);
          }
        },
      });
    };

    onMounted(() => {
      loadTaskTypes();
    });

    return {
      searchList,
      filterParams,
      tableColumns,
      tableRef,
      loadTableData,
      onRefreshTable,
      handleDownload,
      handleRemove,
    };
  },
});
